import { useLocale } from 'next-intl';
import { useRef, useState } from 'react';
import { useSaveTrip } from '~/components/Common/SaveButton/src/useSaveTrip';
import { PENDING_SAVE_TRIP_KEY, type PendingSaveTrip } from '~/constants/savedTrips';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundCurrentFullTrip, usePlaygroundCurrentSkeletonTrip, usePlaygroundStore } from '~/stores/playgroundStore';
import type { JourneyTransformed } from '~/types/Journey.types';
import { trackEvent } from '~/utils';
import { useSharedRouter } from '~/utils/nextMigration/router';
export type SaveTripError = 'build' | 'save' | null;
const journeyPageUrl = (tripId: string) => globalThis.window === undefined ? '' : `${globalThis.location.origin}/journey/${tripId}`;
export const useBuildThenSave = () => {
  const router = useSharedRouter();
  const locale = useLocale() as Locale;
  const skeletonTrip = usePlaygroundCurrentSkeletonTrip();
  const fullTrip = usePlaygroundCurrentFullTrip();
  const playgroundContext = usePlaygroundContext();
  const {
    setFullTrip
  } = usePlaygroundActions();
  const buildJourneyFromSkeleton = api.playground.buildJourneyFromSkeleton.useMutation();
  const savedTripId = fullTrip?.uniqueId ?? skeletonTrip?.uniqueId ?? '';
  const {
    isTripSaved,
    toggleSave
  } = useSaveTrip({
    tripId: savedTripId
  });
  const [isBuilding, setIsBuilding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<SaveTripError>(null);
  const inFlightRef = useRef(false);
  const currentOnScreenSkeleton = () => {
    const {
      skeletonTrips,
      currentTripIndex
    } = usePlaygroundStore.getState();
    return skeletonTrips[currentTripIndex];
  };
  const buildFullTrip = async (): Promise<JourneyTransformed> => {
    const csUser = globalThis.sessionStorage?.getItem('csUser') ?? '';
    const builtTrip = await buildJourneyFromSkeleton.mutateAsync({
      csUser,
      playgroundInputContext: playgroundContext,
      skeletonTripId: skeletonTrip.uniqueId,
      locale
    });
    if (!builtTrip?.items.length) {
      throw new Error('invalid trip - no items');
    }
    if (currentOnScreenSkeleton() === skeletonTrip) {
      setFullTrip(skeletonTrip.uniqueId, builtTrip);
    }
    return builtTrip;
  };
  const resolveBuiltTrip = async (): Promise<JourneyTransformed | null> => {
    if (fullTrip?.items.length) {
      return fullTrip;
    }
    setIsBuilding(true);
    try {
      return await buildFullTrip();
    } catch {
      setError('build');
      return null;
    } finally {
      setIsBuilding(false);
    }
  };
  const save = async () => {
    if (inFlightRef.current || !skeletonTrip) return;
    inFlightRef.current = true;
    setError(null);
    const startingSkeleton = skeletonTrip;
    try {
      const builtTrip = await resolveBuiltTrip();
      if (!builtTrip) return;
      if (currentOnScreenSkeleton() !== startingSkeleton) return;
      setIsSaving(true);
      try {
        await toggleSave({
          tripId: builtTrip.uniqueId,
          tripPageUrl: journeyPageUrl(builtTrip.uniqueId),
          startDate: builtTrip.details.startDate,
          endDate: builtTrip.details.endDate
        });
        trackEvent('maps-save-itinerary', {
          event_category: 'Playground',
          event_label: 'maps-save-itinerary',
          trip_id: builtTrip.uniqueId
        });
      } catch {
        setError('save');
      } finally {
        setIsSaving(false);
      }
    } finally {
      inFlightRef.current = false;
    }
  };
  const deferSave = async () => {
    if (inFlightRef.current || !skeletonTrip) return;
    inFlightRef.current = true;
    setError(null);
    const startingSkeleton_0 = skeletonTrip;
    try {
      const builtTrip = await resolveBuiltTrip();
      if (!builtTrip) return;
      if (currentOnScreenSkeleton() !== startingSkeleton_0) return;
      const pending: PendingSaveTrip = {
        tripId: builtTrip.uniqueId,
        tripPageUrl: journeyPageUrl(builtTrip.uniqueId),
        startDate: builtTrip.details.startDate,
        endDate: builtTrip.details.endDate
      };
      globalThis.sessionStorage?.setItem(PENDING_SAVE_TRIP_KEY, JSON.stringify(pending));
      router.push(`/auth/login?returnTo=/account?tripId=${builtTrip.uniqueId}`);
    } finally {
      inFlightRef.current = false;
    }
  };
  const unsave = async () => {
    if (inFlightRef.current || !savedTripId) return;
    inFlightRef.current = true;
    setIsSaving(true);
    try {
      await toggleSave();
    } catch {
      setError('save');
    } finally {
      setIsSaving(false);
      inFlightRef.current = false;
    }
  };
  const clearError = () => setError(null);
  return {
    save,
    unsave,
    deferSave,
    isBuilding,
    isSaving,
    isSaved: isTripSaved === true,
    error,
    clearError
  };
};